import { z } from 'zod';

// ─── Password strength ──────────────────────────────────────────────
// Shared by the API (set-password) and the web app (set / reset password
// forms) so the rules can never drift between client and server.

// NIST 800-63B: require at least 8, permit long passphrases (>= 64), and do
// NOT impose composition rules — screen against common/predictable instead.
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 64;

/**
 * Lowercased, normalized exact-match blocklist of the most common throwaway
 * passwords. Kept intentionally small — this rejects the obvious offenders,
 * not a full breach corpus.
 */
const COMMON_PASSWORDS: ReadonlySet<string> = new Set([
  'password',
  'password1',
  'passw0rd',
  'qwerty',
  'qwertyuiop',
  'azerty',
  'abc123',
  'qwerty123',
  'letmein',
  'welcome',
  'admin',
  'iloveyou',
  'monkey',
  'dragon',
  'football',
  'baseball',
  'sunshine',
  'princess',
  'superman',
  'changeme',
  'secret',
  'login',
  'starwars',
  'whatever',
]);

/** Character lines used to detect straight ascending/descending runs. */
const SEQUENCE_LINES: readonly string[] = [
  '0123456789',
  'abcdefghijklmnopqrstuvwxyz',
  'qwertyuiop',
  'asdfghjkl',
  'zxcvbnm',
];

/** Reason a password was rejected — maps to a user-facing message per surface. */
export type PasswordWeakness = 'tooShort' | 'tooLong' | 'common' | 'repeated' | 'sequential';

function isAllSameCharacter(value: string): boolean {
  return [...value].every((char) => char === value[0]);
}

function isSequentialRun(value: string): boolean {
  const reversed = [...value].reverse().join('');
  return SEQUENCE_LINES.some((line) => line.includes(value) || line.includes(reversed));
}

/**
 * Returns the first reason a password is unacceptable, or `null` if it passes
 * the hard requirements. Length is checked first so callers can surface a
 * distinct too-short / too-long message.
 */
export function checkPasswordStrength(password: string): PasswordWeakness | null {
  if (password.length < PASSWORD_MIN_LENGTH) return 'tooShort';
  if (password.length > PASSWORD_MAX_LENGTH) return 'tooLong';

  const normalized = password.toLowerCase();
  if (COMMON_PASSWORDS.has(normalized)) return 'common';
  if (isAllSameCharacter(password)) return 'repeated';
  if (isSequentialRun(normalized)) return 'sequential';

  return null;
}

export function isStrongPassword(password: string): boolean {
  return checkPasswordStrength(password) === null;
}

/** Default English messages — used server-side where there is no i18n layer. */
export const PASSWORD_WEAKNESS_MESSAGES: Record<PasswordWeakness, string> = {
  tooShort: `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
  tooLong: `Password must be ${PASSWORD_MAX_LENGTH} characters or fewer`,
  common: 'This password is too common — choose something harder to guess',
  repeated: 'Avoid repeating the same character — choose something harder to guess',
  sequential: 'Avoid simple sequences — choose something harder to guess',
};

// ─── Strength estimate (advisory meter) ─────────────────────────────
// Length-weighted, composition-aware-but-not-required. Length is the primary
// driver per NIST guidance; character variety only nudges the score.

export type PasswordStrengthLabel = 'weak' | 'fair' | 'good' | 'strong';

export interface PasswordStrength {
  /** 0 = fails the hard rules; 1–4 = weak → strong. */
  score: 0 | 1 | 2 | 3 | 4;
  label: PasswordStrengthLabel;
}

const STRENGTH_LABELS: readonly PasswordStrengthLabel[] = ['weak', 'fair', 'good', 'strong'];

function countCharacterClasses(password: string): number {
  let classes = 0;
  if (/[a-z]/.test(password)) classes += 1;
  if (/[A-Z]/.test(password)) classes += 1;
  if (/[0-9]/.test(password)) classes += 1;
  if (/[^a-zA-Z0-9]/.test(password)) classes += 1;
  return classes;
}

/**
 * Estimates password strength for an advisory meter. A password that fails the
 * hard rules scores 0; otherwise it scores 1–4, driven mainly by length so a
 * long all-lowercase passphrase still reads as strong.
 */
export function scorePasswordStrength(password: string): PasswordStrength {
  if (password.length === 0 || checkPasswordStrength(password) !== null) {
    return { score: 0, label: 'weak' };
  }

  const { length } = password;
  const classes = countCharacterClasses(password);

  let score = 1;
  if (length >= 10) score += 1;
  if (length >= 14) score += 1;
  if (length >= 18 || (length >= 12 && classes >= 3)) score += 1;
  // A short, low-variety password shouldn't masquerade as strong.
  if (length < 12 && classes < 2) score = Math.min(score, 2);

  const clamped = Math.min(score, 4) as 1 | 2 | 3 | 4;
  return { score: clamped, label: STRENGTH_LABELS[clamped - 1] };
}

/**
 * Zod schema enforcing both the length floor and the weak-password rules.
 * Reuse this anywhere a new password is accepted.
 */
export const StrongPasswordSchema = z.string().superRefine((password, ctx) => {
  const weakness = checkPasswordStrength(password);
  if (weakness) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: PASSWORD_WEAKNESS_MESSAGES[weakness] });
  }
});
